import { FormikValues } from "formik";
import Accordion from "../../Accordion/Accordion";
import Button from "../../Button/Button";
import { toast } from "react-toastify";
import {
  useJobAdsGenerateAiContent,
  useJobAdsGetAiTemplateTones,
} from "../../../api/JobAds/JobAds";
import Loader from "../../Loader/Loader";
import MarkdownWrapper from "../../Markdown/Markdown";
import { handleDateAsTimestamp } from "../../../utilities/dates/handleDateAsTimestamp";
import { format } from "date-fns";
import useErrorReporting from "../../../hooks/useErrorReporting";
import { JobAdsAiContentResponse } from "../../../api/JobAds/interfaces";
import Modal from "../../Modal/Modal";
import { useMemo, useState } from "react";
import Dropdown from "../../Dropdown/Dropdown";
import { DropdownItem } from "../../Dropdown/interfaces";
import Checkbox from "../../Inputs/Checkbox";

interface JobAdAiTemplateProps {
  adId: string;
  templateData: JobAdsAiContentResponse;
  values: FormikValues;
  setFieldTouched: (field: string) => void;
  setFieldValue: (field: string, value: unknown) => void;
}

type SectionLists =
  | "Job_Benefits"
  | "Job_Responsibilities"
  | "Job_Intro"
  | "Job_Requirements"
  | "Job_Description"
  | "Job_Terms";

interface TemplateAiOptionsState {
  tone: string;
  sections: {
    [k in SectionLists]: boolean;
  };
}

const JobAdAiTemplate = ({
  adId,
  templateData,
  values,
  setFieldTouched,
  setFieldValue,
}: JobAdAiTemplateProps) => {
  const errorReporting = useErrorReporting();
  const { is_generating, ai_template, date } = templateData;

  /*===================================
    GENERATE AI CONTENT 
  ====================================*/
  const generateAIContent = useJobAdsGenerateAiContent(adId);
  const [templateAiOptions, setTemplateAiOptions] = useState<TemplateAiOptionsState>({
    tone: "professional",
    sections: {
      Job_Benefits: false,
      Job_Responsibilities: false,
      Job_Intro: false,
      Job_Requirements: false,
      Job_Description: false,
      Job_Terms: false,
    },
  });

  const handleGenerateAI = async () => {
    if (!adId) {
      toast.error("Please target a valid job ad for AI template (re)generation.");
      return;
    }

    try {
      // Trigger fields validation
      setFieldTouched("headline_1");
      setFieldTouched("job_title");

      if (!values.job_title || !values.headline_1) {
        toast.info("Please fill out the required fields above.");
        return;
      }

      // Extract only those list sections that were selected by the user
      const list_sections: string[] = Object.entries(templateAiOptions.sections)
        .map(entry => {
          const [key, value] = entry;
          if (!value) return "";
          return key;
        })
        .filter(section => section);

      await generateAIContent.mutateAsync({
        job_title: values.job_title,
        headline_1: values.headline_1,
        headline_2: values.headline_2,
        headline_3: values.headline_3,
        job_questions: values.job_questions,
        job_intro: values.job_intro,
        job_description: values.job_description,
        job_type: values.job_type,
        job_benefits: values.job_benefits,
        job_responsibilities: values.job_responsibilities,
        job_requirements: values.job_requirements,
        job_terms: values.job_terms,
        address: {
          city: values.address_city,
          state: values.address_state,
          country: values.address_country,
          street: values.address_street,
          zip: values.address_zip,
        },
        tone: templateAiOptions.tone,
        list_sections: list_sections,
      });
    } catch (error) {
      errorReporting("Failed generating AI template for targeted job ad", error, {
        adId: adId,
      });
    }
  };

  /*===================================
    USE THE GENERATED AI TEMPLATE
    AND INSERT IT INTO THE TEXT EDITOR
  ====================================*/
  const [templateModalIsOpen, setTemplateModalIsOpen] = useState<boolean>(false);
  const handleUseGeneratedAiTemplate = async () => {
    try {
      const markdownWrapper = document.querySelector(".markdown-wrapper");
      const htmlContent = markdownWrapper?.innerHTML;
      setFieldValue("content", htmlContent);

      // Close the modal if it was previously opened by the user
      if (templateModalIsOpen) setTemplateModalIsOpen(false);
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  if (!ai_template && !is_generating) {
    return (
      <Accordion title="AI Template" isOpenByDefault={true} duration={400}>
        <div className="d-flex flex-column flex-sm-row justify-content-start justify-content-sm-between align-items-start align-items-sm-center">
          <p className="accordion__empty-message">No generated AI template to display.</p>

          <Button
            type="button"
            modifierClass="btn--primary btn--fluid btn--fluid--md"
            onClick={handleGenerateAI}
            isDisabled={generateAIContent.isPending}
          >
            Generate AI Template
          </Button>
        </div>

        <AiTemplateOptions
          templateAiOptions={templateAiOptions}
          handleUpdateAiOptions={aiOptions => setTemplateAiOptions(aiOptions)}
        />
      </Accordion>
    );
  }

  if (!ai_template && is_generating) {
    return (
      <Accordion title="AI Template" isOpenByDefault={true} duration={400}>
        <div className="d-flex align-items-center">
          <Loader size="sm" modifierWrapper="mr--15" />
          <p className="accordion__empty-message accordion__empty-message--is-last-element">
            AI template is being generated...
          </p>
        </div>
      </Accordion>
    );
  }

  return (
    <Accordion title="AI Template" isOpenByDefault={true} duration={400}>
      <MarkdownWrapper markdown={ai_template!} modifierClass="mb--30" />
      <hr className="d-block mb--20i" />

      <div className="d-flex flex-column flex-sm-row justify-content-center justify-content-sm-between align-items-center align-items-sm-start">
        <Button
          type="button"
          modifierClass="btn--primary btn--fluid btn--fluid--md mb--5 order-2 order-sm-1"
          isDisabled={generateAIContent.isPending}
          onClick={() => {
            if (values.content) {
              setTemplateModalIsOpen(true);
            } else {
              handleUseGeneratedAiTemplate();
            }
          }}
        >
          Use Template
        </Button>

        <div className="d-flex flex-column align-items-end order-1 order-sm-2">
          <Button
            type="button"
            modifierClass="btn--primary btn--fluid btn--fluid--md mb--5"
            onClick={handleGenerateAI}
            isDisabled={generateAIContent.isPending}
          >
            Regenerate AI Template
          </Button>
          <p className="mb--0 mb--sm--15 txt--center--sm txt--sm">
            Last Generated Date:{" "}
            {date ? format(new Date(handleDateAsTimestamp(date)), "MM/dd/yyyy hh:mm a") : "Never"}
          </p>
        </div>
      </div>

      <AiTemplateOptions
        templateAiOptions={templateAiOptions}
        handleUpdateAiOptions={aiOptions => setTemplateAiOptions(aiOptions)}
      />

      {templateModalIsOpen ? (
        <Modal
          title="Use Template"
          text="Are you sure you want to use the AI generated template? This will overwrite the current content of the text editor."
          handleCloseModal={() => setTemplateModalIsOpen(false)}
          modifierClass="modal--md"
        >
          <div className="modal__actions">
            <Button
              modifierClass="btn--fluid btn--primary"
              onClick={() => setTemplateModalIsOpen(false)}
              type="button"
            >
              Cancel
            </Button>

            <Button
              modifierClass="txt--capitalize btn--fluid btn--secondary"
              type="button"
              onClick={handleUseGeneratedAiTemplate}
            >
              Yes, Use Template
            </Button>
          </div>
        </Modal>
      ) : null}
    </Accordion>
  );
};

function AiTemplateOptions({
  templateAiOptions,
  handleUpdateAiOptions,
}: {
  templateAiOptions: TemplateAiOptionsState;
  handleUpdateAiOptions: (options: TemplateAiOptionsState) => void;
}) {
  const { data: tones, isPending: tonesIsPending } = useJobAdsGetAiTemplateTones();

  const AI_TEMPLATE_TONES = useMemo(() => {
    if (!tones || !tones.length || tonesIsPending) return [];
    return tones.map(tone => {
      return { text: tone.name, value: tone.slug };
    });
  }, [tones, tonesIsPending]);

  return (
    <div className="job-ads-edit__ai-template-options">
      <h3 className="mb--20">AI Templating Options</h3>

      <Dropdown
        title="Professional"
        preselectedItemValue={templateAiOptions.tone}
        label="Tone"
        items={AI_TEMPLATE_TONES}
        isLoading={tonesIsPending}
        disabled={tonesIsPending}
        handleItemSelected={(tone: DropdownItem) =>
          handleUpdateAiOptions({
            ...templateAiOptions,
            tone: tone.value as string,
          })
        }
        modifierClass="mb--20"
      />

      <h5>List Sections</h5>
      <p>
        Select the sections of the generated job ad for which the content should be presented as a
        listing.
      </p>

      <div className="job-ads-edit__ai-template-options__list-sections">
        {Object.entries(templateAiOptions.sections).map(section => (
          <div>
            <Checkbox
              id={section[0]}
              name={section[0]}
              label={section[0].split("_").join(" ")}
              value={section[0]}
              isChecked={section[1]}
              handleCheckboxAction={(checkboxValue: React.ChangeEvent<HTMLInputElement>) => {
                const targetName = checkboxValue.target.name as SectionLists;
                handleUpdateAiOptions({
                  ...templateAiOptions,
                  sections: {
                    ...templateAiOptions.sections,
                    [targetName]: !templateAiOptions.sections[targetName],
                  },
                });
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export default JobAdAiTemplate;
